import { Box, IconButton, LinearProgress, Typography } from "@mui/material";
import axios from "axios";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import * as XLSX from "xlsx"; // ✅ Import XLSX to read CSV/Excel files
import CloseCircleIcon from "./icons/CloseCircleIcon";
import CsvIcon from "./icons/CsvIcon";
import FileIcon from "./icons/FileIcon";

const backendURL = process.env.REACT_APP_API_URL; // Backend URL from .env

const DragDropUploader = ({ onUploadSuccess, listType, setMessage }) => {
  const userId = localStorage.getItem("userId"); // Retrieve userId

  // ✅ State for Progress Bar
  const [progress, setProgress] = useState(10);
  const [isUploading, setIsUploading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileSize, setFileSize] = useState(0);

  // ✅ Handle File Drop (Triggers Upload Automatically)
  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!listType) {
        setMessage("❌ Please select a list type before uploading a file.");
        return;
      }
      const file = acceptedFiles[0];
      if (!file) return;

      // ✅ Read file and count rows before uploading
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        // ✅ Count rows (excluding header)
        const rowCount = jsonData.length - 1;

        if (rowCount > 20000) {
          setMessage(
            "❌ Error: File exceeds 20,000 rows. Please upload a smaller file.",
          );
          return;
        }

        // ✅ File is valid, proceed with upload
        const formData = new FormData();
        formData.append("file", file);
        formData.append("user_id", userId);
        formData.append("list_type", listType); // Ensure list_type is included

        console.log(
          "⬆️ Uploading file:",
          file.name,
          "with list type:",
          listType,
        );

        // ✅ Start showing progress bar
        setIsUploading(true);
        setFileName(file.name);
        setFileSize(file.size);
        setProgress(0);

        try {
          const response = await axios.post(`${backendURL}/upload`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              console.log(`📊 Upload Progress: ${percentCompleted}%`); // ✅ Debugging log
              setProgress(percentCompleted); // ✅ Update progress bar state
            },
          });

          if (response.status === 200) {
            console.log("✅ File uploaded successfully:", response.data);
            onUploadSuccess(response.data); // Notify parent component about success
            setMessage(""); // ✅ Clear any previous error message
          }
        } catch (error) {
          console.error("❌ Error uploading file:", error);

          if (error.response && error.response.data) {
            // ✅ Handle mismatched list type error from backend
            if (error.response.data.detected_type) {
              const expectedList =
                listType === "property_radar" ? "Property Radar" : "PropStream";
              setMessage(`❌ Please upload a ${expectedList} list.`);
            } else {
              setMessage(`❌ ${error.response.data.message}`);
            }
          } else {
            setMessage("❌ Failed to upload file. Please try again.");
          }
        } finally {
          setIsUploading(false); // ✅ Hide progress bar after upload completes (success or failure)
        }
      };

      reader.readAsArrayBuffer(file);
    },
    [userId, listType, onUploadSuccess],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: ".csv",
    disabled: !listType, // ✅ Prevent file selection if no list type is selected
  });

  return (
    <>
      {isUploading ? (
        <Box
          display={"flex"}
          alignItems={"center"}
          gap={"20px"}
          sx={{
            border: "1px solid #D7D7D7",
            borderRadius: "10px",
            p: "10px 20px",
            bgcolor: "rgba(255, 255, 255, 0.2)",
          }}
        >
          <CsvIcon style={{ flexShrink: 0 }} />

          <Box maxWidth={"16.25rem"} flexGrow={1} minWidth={0}>
            <Typography
              fontSize={"14px"}
              variant="body8"
              component={"p"}
              color="#333"
              lineHeight={1}
              noWrap
            >
              {fileName}
            </Typography>
            <LinearProgress
              value={progress}
              variant="determinate"
              color="brand"
              sx={{ mt: 1 }}
            />
            <Typography
              fontSize={"14px"}
              variant="body8"
              component={"p"}
              color="rgba(130, 130, 130, 0.6)"
              mt={0.5}
            >
              {progress}% - {fileSize / 1000}kB
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={() => setIsUploading(false)}
            color="#999"
            sx={{ ":hover": { color: "#333" }, m: "0 -5px 0 auto" }}
          >
            <CloseCircleIcon />
          </IconButton>
        </Box>
      ) : (
        <>
          <Box
            {...getRootProps()}
            sx={{
              border: "1px dashed #8B8B8B",
              borderRadius: "10px",
              p: "30px",
              textAlign: "center",
              cursor: "pointer",
              bgcolor: "rgba(255, 255, 255, 0.2)",
            }}
          >
            <input {...getInputProps()} />
            <FileIcon />
            <Typography
              fontSize={"14px"}
              mt={1}
              variant="body8"
              component={"p"}
              color="#7B7979"
            >
              <span style={{ fontWeight: 600, color: "#333" }}>
                Drag & drop file to upload
              </span>{" "}
              <br /> or{" "}
              <span style={{ textDecoration: "underline", color: "#F69133" }}>
                browse file
              </span>{" "}
              on your computer
            </Typography>
          </Box>
          <Typography
            mt={1}
            variant="caption2"
            color="#828282"
            display={"flex"}
            justifyContent={"space-between"}
          >
            <span>Format: .CSV only</span>
            <span>Max. size: 20,000 rows</span>
          </Typography>
        </>
      )}
    </>
  );
};

export default DragDropUploader;
